import { DishesCarousel } from 'components/carousels/dishes-carousel';
import { ShefsCarousel } from 'components/carousels/shefs-carousel';
import { AllLinksSection } from 'components/city-blocks/all-links-section';
import { HeaderCopy } from 'components/city-blocks/header-copy';
import { MaxWidthContainer } from 'components/city-blocks/max-width-container';
import { UnderlineButton } from 'components/dynamic-content/button';
import { generateHeroText } from 'components/dynamic-content/hero-copy';
import { generateRegionContentCopy } from 'components/dynamic-content/region-copy';
import { WavyDivider } from 'components/flair/WavyDivider';
import { HeroBlock } from 'components/hero-block/hero-block';
import Layout from 'components/layout';
import Head from 'next/head';
import Link from 'next/link';
import React from 'react';
import { getRegionDisplayName, RegionGrammarRules } from 'shared/baby-grammar-rules';
import { getAssetPath } from 'utils/asset-utils';
import { Crumb, generateRegionPageBreadcrumbs } from 'utils/breadcrumb-util';
import { BASE_PATH } from 'utils/constants';
import { getFrontEndUrl } from 'utils/environment-util';
import { CityPageDishFragment, CityPageShefFragment } from 'utils/gqlReactTypings.generated.d';
import { ComputedRegion, ShallowCityPage } from 'utils/types';

export const LINKS_BLOCK_LIMIT = 5;

interface IProps {
  region: ComputedRegion;
  dishes?: CityPageDishFragment[];
  shefs?: CityPageShefFragment[];
  nearbyCities?: ShallowCityPage[];
}

// NOTE: Making a pattern of functions for root pages
const HEADER_COPY_TITLE = 'Check out our food delivery areas below!';
export const invokeHeaderCopyContent = (formattedDisplayName: string): string =>
  `We deliver in ${formattedDisplayName}! Browse the top locations for delivery, or click through to see all cities, neighborhood and ZIPs where we offer homemade food delivery!`;
const DISH_POSITIONING_COPY = 'Browse the most popular homemade dishes in metropolitan areas where Shef cooks!';
const SHEF_POSITIONING_COPY = 'Find a local Shef in a metropolitan area near you for delicious homemade dishes!';

export const RegionPageLayout: React.FC<IProps> = ({ region, dishes, shefs }) => {
  const displayName = getRegionDisplayName(region);
  const firstZipCode = region.nearbyZipCodes?.length && region.nearbyZipCodes[0].name;
  const explorePageLink = firstZipCode ? `${getFrontEndUrl()}/explore/zip/${firstZipCode}` : getFrontEndUrl();

  const title = `${displayName} Homemade Food Delivered Fresh!`;
  const metaDesc = `Shef delivers delicious homemade food in ${displayName}. Hundreds of Shefs. 1,000s of authentic dishes to choose from. Find your cuisine, order today!`;
  const crumbs: Crumb[] = generateRegionPageBreadcrumbs(region);

  const regionContentCopy = generateRegionContentCopy(region);

  return (
    <Layout>
      <Head>
        <meta name='og:title' content={title} />
        <title>{title}</title>
        <meta name='description' content={metaDesc} />
      </Head>
      <HeroBlock
        className='mb-10'
        backgroundImage={getAssetPath('/images/CDN/cities_banner.png')}
        heroText={generateHeroText(displayName)}
        crumbs={crumbs}
      />
      <MaxWidthContainer className='px-4 md:px-0'>
        <HeaderCopy className='mb-10' title={HEADER_COPY_TITLE} content={invokeHeaderCopyContent(displayName)} />

        <div className='mb-10 flex flex-col gap-8 md:gap-10'>
          <div className='mb-8 flex flex-col gap-6 md:mb-16 md:gap-8'>
            <div className='flex w-full flex-row items-start justify-between gap-4'>
              <div className='flex flex-col items-start justify-start'>
                <div className='text-red-950 leading-trim-both text-edge-cap justify-start text-xl font-bold leading-[120%] sm:text-2xl md:text-3xl'>
                  Top cities {RegionGrammarRules[region.name]?.useNear ? 'near' : 'in'} {displayName}
                </div>
              </div>
              <div className='flex w-36 flex-col items-end justify-end'>
                <UnderlineButton
                  link={`${BASE_PATH}/cities-we-serve`}
                  text='See all'
                  className='w-fit'
                  underlineWidth='47'
                />{' '}
              </div>
            </div>
            <div className='grid w-full grid-cols-2 gap-3 md:flex md:flex-wrap md:gap-4'>
              {region.nearbyCities?.slice(0, LINKS_BLOCK_LIMIT).map((location) => (
                <Link key={location.href} href={location.href}>
                  <div className='inline-flex w-full items-center justify-between rounded-lg bg-white p-4 shadow-[10px_8px_16px_0px_rgba(0,0,0,0.10)] outline outline-1 outline-offset-[-1px] outline-neutral-100 md:w-80'>
                    <div className='text-red-950 justify-center text-sm font-medium leading-relaxed sm:text-base md:text-lg'>
                      {location.name}
                    </div>
                    <i className='fa fa-chevron-right text-red-950 text-sm' />
                  </div>
                </Link>
              ))}
            </div>
          </div>

          <div className='mb-8 flex flex-col gap-6 md:mb-16 md:gap-8'>
            <div className='flex w-full flex-row items-start justify-between gap-4'>
              <div className='flex flex-col items-start justify-start'>
                <div className='text-red-950 leading-trim-both text-edge-cap justify-start text-xl font-bold leading-[120%] sm:text-2xl md:text-3xl'>
                  Top ZIPs {RegionGrammarRules[region.name]?.useNear ? 'near' : 'in'} {displayName}
                </div>
              </div>
              <div className='flex w-24 flex-col items-end justify-end'>
                <UnderlineButton
                  link={`${BASE_PATH}/zip-codes-we-serve`}
                  text='See all'
                  className='w-fit'
                  underlineWidth='47'
                />{' '}
              </div>
            </div>
            <div className='grid w-full grid-cols-2 gap-3 md:flex md:flex-wrap md:gap-4'>
              {region.nearbyZipCodes?.slice(0, LINKS_BLOCK_LIMIT).map((location) => (
                <Link key={location.href} href={location.href}>
                  <div className='inline-flex w-full items-center justify-between rounded-lg bg-white p-4 shadow-[10px_8px_16px_0px_rgba(0,0,0,0.10)] outline outline-1 outline-offset-[-1px] outline-neutral-100 md:w-80'>
                    <div className='text-red-950 justify-center text-sm font-medium leading-relaxed sm:text-base md:text-lg'>
                      {location.name}
                    </div>
                    <i className='fa fa-chevron-right text-red-950 text-sm' />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
        <WavyDivider className='pb-10 md:pb-10' />

        {regionContentCopy && (
          <>
            <div className='mb-10'>{generateRegionContentCopy(region)}</div>
            <WavyDivider className='pb-10 md:pb-20' />
          </>
        )}
      </MaxWidthContainer>
      <DishesCarousel
        className='mb-20'
        dishes={dishes}
        positioningCopy={DISH_POSITIONING_COPY}
        seeAllHref={explorePageLink}
      />
      <ShefsCarousel
        className='mb-20'
        shefs={shefs}
        positioningCopy={SHEF_POSITIONING_COPY}
        seeAllHref={explorePageLink}
      />

      {region.nearbyCities && (
        <AllLinksSection
          className='mb-4 md:mb-6'
          title={`Cities in ${displayName}`}
          seeAllHref={`${BASE_PATH}/cities-we-serve`}
          seeAllText='See all'>
          {region.nearbyCities?.map((location) => (
            <Link key={location.href} href={location.href}>
              <div className='inline-flex w-full items-center justify-between rounded-lg bg-white p-4 shadow-[10px_8px_16px_0px_rgba(0,0,0,0.10)] outline outline-1 outline-offset-[-1px] outline-neutral-100 md:w-80'>
                <div className='text-red-950 justify-center text-sm font-medium leading-relaxed sm:text-base md:text-lg'>
                  {location.name}
                </div>
                <i className='fa fa-chevron-right text-red-950 text-sm' />
              </div>
            </Link>
          ))}
        </AllLinksSection>
      )}
    </Layout>
  );
};
